import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient, { setTokens, clearTokens, getAccessToken } from '@/services/api'
import { useAccountContextStore } from '@/stores/accountContext'
import type { User, LoginCredentials, RegisterData, AuthTokens } from '@/types'

export type LoginResult = 'success' | 'two_factor_required' | 'step_up_required'

export interface PendingAuth {
  type: 'two_factor' | 'step_up'
  userUuid: string
  email: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const initialized = ref(false)
  const pendingAuth = ref<PendingAuth | null>(null)

  const isAuthenticated = computed(() => !!user.value)
  const userName = computed(() => user.value?.name ?? '')
  const userEmail = computed(() => user.value?.email ?? '')
  const personalAccountUuid = computed(() => user.value?.personal_account_uuid ?? null)

  async function fetchCurrentUser(): Promise<void> {
    const { data } = await apiClient.get<User>('/users/me')
    user.value = data
    useAccountContextStore().hydrateFromUser(data)
  }

  /** Initialize auth state on app load */
  async function initialize(): Promise<void> {
    const token = getAccessToken()
    if (!token) {
      initialized.value = true
      return
    }

    try {
      loading.value = true
      await fetchCurrentUser()
    } catch {
      clearTokens()
      user.value = null
      useAccountContextStore().clear()
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  /** Log in with email and password */
  async function login(credentials: LoginCredentials): Promise<LoginResult> {
    loading.value = true
    try {
      const response = await apiClient.post<AuthTokens & {
        two_factor_required?: boolean
        step_up_required?: boolean
        user_uuid?: string
      }>('/auth/login', credentials)

      const data = response.data

      if (data.two_factor_required && data.user_uuid) {
        pendingAuth.value = {
          type: 'two_factor',
          userUuid: data.user_uuid,
          email: credentials.email,
        }
        return 'two_factor_required'
      }

      if (data.step_up_required && data.user_uuid) {
        pendingAuth.value = {
          type: 'step_up',
          userUuid: data.user_uuid,
          email: credentials.email,
        }
        return 'step_up_required'
      }

      setTokens(data)
      await fetchCurrentUser()
      return 'success'
    } finally {
      loading.value = false
    }
  }

  async function sendStepUpCode(): Promise<void> {
    if (!pendingAuth.value) return
    await apiClient.post('/auth/step-up/send', { user_uuid: pendingAuth.value.userUuid })
  }

  async function completeStepUp(code: string): Promise<LoginResult> {
    if (!pendingAuth.value) {
      throw new Error('No pending authentication')
    }

    loading.value = true
    try {
      const { data } = await apiClient.post<AuthTokens & {
        two_factor_required?: boolean
        user_uuid?: string
      }>('/auth/step-up', {
        user_uuid: pendingAuth.value.userUuid,
        code,
      })

      if (data.two_factor_required && data.user_uuid) {
        pendingAuth.value = {
          type: 'two_factor',
          userUuid: data.user_uuid,
          email: pendingAuth.value.email,
        }
        return 'two_factor_required'
      }

      setTokens(data)
      pendingAuth.value = null
      await fetchCurrentUser()
      return 'success'
    } finally {
      loading.value = false
    }
  }

  async function completeTwoFactor(code: string, _isRecovery = false): Promise<void> {
    if (!pendingAuth.value) {
      throw new Error('No pending authentication')
    }

    loading.value = true
    try {
      const { data } = await apiClient.post<AuthTokens & {
        recovery_codes_remaining?: number
        recovery_codes_low?: boolean
      }>('/auth/2fa/verify', {
        user_uuid: pendingAuth.value.userUuid,
        code,
      })
      setTokens(data)
      pendingAuth.value = null
      await fetchCurrentUser()
      if (data.recovery_codes_remaining !== undefined) {
        const { useNotificationStore } = await import('@/stores/notifications')
        const notifications = useNotificationStore()
        if (data.recovery_codes_low) {
          notifications.warning(
            `Recovery code used. ${data.recovery_codes_remaining} code(s) remaining — generate new codes in Security settings.`,
          )
        } else {
          notifications.info(`Recovery code accepted. ${data.recovery_codes_remaining} code(s) remaining.`)
        }
      }
    } finally {
      loading.value = false
    }
  }

  async function verifyEmailToken(token: string): Promise<void> {
    await apiClient.post('/auth/email/verify', { token })
  }

  /** Register a new account */
  async function register(registerData: RegisterData): Promise<void> {
    loading.value = true
    try {
      await apiClient.post('/auth/register', registerData)
    } finally {
      loading.value = false
    }
  }

  /** Log out and clear session */
  async function logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } catch {
      // Ignore logout errors — clear local state regardless
    } finally {
      clearTokens()
      user.value = null
      pendingAuth.value = null
      useAccountContextStore().clear()
    }
  }

  /** Refresh the current session */
  async function refreshSession(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return

    try {
      const { data } = await apiClient.post<AuthTokens>('/auth/refresh', {
        refresh_token: refreshToken,
      })
      setTokens(data)
    } catch {
      clearTokens()
      user.value = null
      useAccountContextStore().clear()
    }
  }

  function clearPendingAuth(): void {
    pendingAuth.value = null
  }

  return {
    user,
    loading,
    initialized,
    pendingAuth,
    isAuthenticated,
    userName,
    userEmail,
    personalAccountUuid,
    initialize,
    login,
    register,
    logout,
    refreshSession,
    sendStepUpCode,
    completeStepUp,
    completeTwoFactor,
    verifyEmailToken,
    fetchCurrentUser,
    clearPendingAuth,
  }
})
